import React, { useEffect, useState } from "react";
import OrderLinesTable from "../../../../components/Orders/OrderLinesTable";
import axios from "axios";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import { Badge } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  MdCancel,
  MdLocalPrintshop,
  // MdSportsMotorsports,
  MdArrowBackIosNew,
  MdOutlineCreditCardOff,
  MdOutlineCreditCard,
  MdLocalShipping,
  MdUndo,
  MdRefresh,
  MdMap,
} from "react-icons/md";
import {
  assignRestaurantOrderToDeliveryAgent,
  getOrderDetailsToProcess,
  updateRestaurantOrderDeliveryStatus,
  re_assignRestaurantOrderToDeliveryAgent,
} from "../../../../actions/webOrderActions";
import { toast } from "react-toastify";
import { RESET_UPDATE_DELIVERY_STATUS_COMPLETE } from "../../../../constants/webOrderConstants";
import "./RestaurantOrderDetails.css";
import {
  BASEMAP_TILE_URL,
  BASEMAP_ATTRIBUTION,
  BASEMAP_SUBDOMAINS,
  BASEMAP_MAX_ZOOM,
} from "../../../../config/basemap";
import { getUserFromLocalStorage } from "../../../../utilities/user.js";
import RiderSelectionDropdown from "./RiderSelectionDropdown";
import { isRiderAssignmentLocked } from "../../../../utilities/orderRiderLock";
import ThreePLPartnerDropdown from "../../../../components/ThreePLPartnerDropdown";
import CancelOrderFields, { useCancellationReason, CancellationRecord } from "../../../../components/Orders/CancelOrderFields";
import ContactAction from "../../../../components/Orders/ContactAction";
import DropoffMap from "../../../../components/Maps/DropoffMap";
import VendorHandoffActions from "../../../../components/Orders/VendorHandoffActions";
import { openReceiptForPrint } from "../../../../utilities/openReceipt";
import { getShippingAddressText } from "../../../../utilities/orderPresentation";
import OrderNextStep from "../../../../components/Orders/OrderNextStep";
import socketAuthOptions from "../../../../utilities/socketAuth";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import {
  getDeliveryProgress,
  canVendorDispatch,
  canVendorMarkDelivered,
} from "../../../../utilities/orderDeliverySteps";
import { useGate } from "../../../../components/team/Gated";
import AppSelect from "../../../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

const isDevelopment = process.env.NODE_ENV === "development";

const BrandVendorOrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = getUserFromLocalStorage();

  const [showCancelModal, setShowCancelModal] = useState(false);
  // Reason + note for the audit record. Required by the server on cancel.
  const cancelReason = useCancellationReason();
  const [riders, setRiders] = useState([]);
  const [riderId, setRiderId] = useState("");
  // Vendor team permissions: every status, dispatch, rider and 3PL change
  // on this page is orders:manage (components/team/Gated.js).
  const ordersGate = useGate("orders:manage");
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [assignedRider, setAssignedRider] = useState(null);
  const [selectedAssignmentRider, setSelectedAssignmentRider] = useState(null);
  const [elapsedAssignedTime, setElapsedAssignedTime] = useState(0);
  const [elapsedDispatchedTime, setElapsedDispatchedTime] = useState(0);

  // Brand quote order: fulfilment stage panel
  const [stageBusy, setStageBusy] = useState(false);

  // 3PL Partner assignment state
  const [assignmentMode, setAssignmentMode] = useState("rider"); // "rider" or "3pl"
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [assigning3PL, setAssigning3PL] = useState(false);
  const [recalling3PL, setRecalling3PL] = useState(false);
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  const orderDetails = useSelector((state) => state.orderDetails);
  const {
    order,
    loading,
    error,
    isUpdateDeliveryStatusComplete,
  } = orderDetails;

  // Live order news (a rider's pickup, a 3PL delivery, a decline) through the
  // one shared feed; see hooks/useOrderLiveUpdates.js. The page-level
  // listeners this replaces put `{ _id, delivery_status }` in the store as
  // the whole order, blanking the items.
  useOrderLiveUpdates(id, {
    onChange: (fresh) => {
      if (fresh?.delivery_status === "PROCESSING" && !fresh?.riderId) setRiderId("");
    },
  });

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(getOrderDetailsToProcess(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (isUpdateDeliveryStatusComplete) {
      dispatch({ type: RESET_UPDATE_DELIVERY_STATUS_COMPLETE });
    }
  }, [isUpdateDeliveryStatusComplete, dispatch]);

  useEffect(() => {
    if (order?.riderId) {
      setRiderId(
        typeof order.riderId === "object" ? order.riderId._id : order.riderId
      );
    }
  }, [order?.riderId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (order?.riderId && riders.length) {
      setAssignedRider(
        riders.find(
          (rider) =>
            rider._id ===
            (typeof order.riderId === "object" ? order.riderId._id : order.riderId)
        )
      );
    }
  }, [order, riders]);

  useEffect(() => {
    if (order && order.orderItems && order.orderItems.length > 0) {
      setIsDataLoaded(true);
    }
  }, [order]);

  const socketUrl = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    // Authenticated: without a token the server treats this socket as
    // anonymous, and every gated handler (joinVendorRoom, getRiders scoping)
    // silently refuses. See utilities/socketAuth.js.
    const socket = io(
      socketUrl,
      socketAuthOptions({ userId: user?.result?._id })
    );

    socket.on("connect", () => {
      if (isDevelopment) {
        console.log("Webclient Connected");
      }
      socket.emit("getRiders");
    });

    socket.on("initialRidersList", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    socket.on("lusukuRiders", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    socket.on("updatedRidersList", (updatedRiders) => {
      setRiders(updatedRiders);
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.result?._id]);

  const handleBack = () => {
    history.push("/admin/brand-quotes");
  };

  const QUOTE_STAGE_ACTIONS = [
    { from: "AWAITING_VENDOR_FULFILMENT", stage: "VENDOR_CONFIRMED", label: "Confirm Order",   variant: "contained", color: "#0a578b"    },
    { from: "VENDOR_CONFIRMED",           stage: "DISPATCHED",       label: "Mark Dispatched", variant: "contained", color: "#0284c7"    },
    { from: "DISPATCHED",                 stage: "DELIVERED",        label: "Mark Delivered",  variant: "contained", color: "#16a34a"    },
  ];

  const advanceQuoteOrderStage = async (stage) => {
    setStageBusy(true);
    try {
      const token = user?.token;
      await axios.patch(
        `${API_URL}/api/brands/orders/${order?._id}/stage`,
        { stage },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Order stage updated: ${stage.replace(/_/g, " ")}`);
      dispatch(getOrderDetailsToProcess(id));
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update stage");
    } finally {
      setStageBusy(false);
    }
  };

  const handleShowMap = () => setShowMapModal(true);
  const handleCloseMap = () => setShowMapModal(false);

  const handleUpdateStatus = (newStatus) => {
    if (newStatus === "ASSIGNED" && !riderId) {
      toast.warn("Please select a rider before assigning the order.");
      return;
    }
    if (newStatus === "ASSIGNED") {
      assignBrandOrderToRider();
    } else {
      dispatch(updateRestaurantOrderDeliveryStatus(order?._id, newStatus));
    }
  };

  const handleCancelOrder = () => {
    // Refuse locally too, so the modal explains itself instead of
    // bouncing off a 400 from the server.
    if (!cancelReason.validate()) return;
    const delivery_status = "REJECTED";
    
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status, cancelReason.payload()));
    setShowCancelModal(false);
    cancelReason.reset();
  };

  // The receipt document (seller, TIN, customer, payment, lines), not a
  // print of this page.
  const handlePrint = () => openReceiptForPrint(order?._id);

  const assignBrandOrderToRider = () => {
    const delivery_status = "ASSIGNED";
    const assignedTime = Date.now();
    const selectedRider =
      (selectedAssignmentRider?._id === riderId && selectedAssignmentRider) ||
      riders.find((r) => r._id === riderId);
    const isCodFlagged = selectedRider?.codCash?.isFlagged === true;
    toast.success(`Assigning order ${order?._id} to rider ${riderId}`);
    localStorage.setItem(`order_${order?._id}_assignedTime`, assignedTime);
    dispatch(
      assignRestaurantOrderToDeliveryAgent(
        order?._id,
        riderId,
        delivery_status,
        isCodFlagged
      )
    );
  };

  const handleDispatchBrandOrder = () => {
    const delivery_status = "DISPATCHED";
    const dispatchedTime = Date.now();
    console.log(`Dispatching order ${order?._id}`);
    localStorage.setItem(`order_${order?._id}_dispatchedTime`, dispatchedTime);
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleDeliverBrandOrder = () => {
    const delivery_status = "DELIVERED";
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleReassignOrder = () => {
    const delivery_status = "PROCESSING";
    const currentRiderId =
      typeof order?.riderId === "object" ? order?.riderId?._id : order?.riderId;
    if (order?._id && currentRiderId) {
      dispatch(
        re_assignRestaurantOrderToDeliveryAgent(
          order?._id,
          currentRiderId,
          delivery_status,
          true
        )
      );
    } else {
      toast.error("No rider currently assigned to this order.");
    }
  };

  // Assign order to 3PL Partner
  const assignOrderTo3PLPartner = async () => {
    if (!selectedPartnerId) {
      toast.warn("Please select a 3PL partner");
      return;
    }

    setAssigning3PL(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/assign-to-3pl`,
        { partnerId: selectedPartnerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Order assigned to 3PL partner!");
        dispatch(getOrderDetailsToProcess(order?._id));
      } else {
        toast.error(response.data.error?.message || "Failed to assign order");
      }
    } catch (error) {
      console.error("Error assigning to 3PL:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to assign to 3PL partner"
      );
    } finally {
      setAssigning3PL(false);
    }
  };

  // Recall order from 3PL Partner
  const recallOrderFrom3PL = async () => {
    setShowRecallModal(false);
    setRecalling3PL(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/recall-from-3pl`,
        { reason: "Recalled by vendor" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Order recalled successfully!");
        dispatch(getOrderDetailsToProcess(order?._id));
      } else {
        toast.error(response.data.error?.message || "Failed to recall order");
      }
    } catch (error) {
      console.error("Error recalling from 3PL:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to recall order from 3PL partner"
      );
    } finally {
      setRecalling3PL(false);
    }
  };

  // The delivery ladder lives in utilities/orderDeliverySteps.js so this page,
  // its sibling vendor pages and the next-step banner can never disagree about
  // where an order is. This was a four-case switch whose `default: 0` emptied
  // the bar for the six statuses this page never writes itself: ACCEPTED and
  // PICKED_UP come from the rider app (OrderDetailsScreen / DispatchOrderScreen),
  // PENDING_3PL / IN_TRANSIT / ARRIVED from the 3PL rail, FULFILLED from the
  // counter. A rider tapping Accept therefore made the vendor's screen look
  // like the order had gone backwards to zero.
  //
  // NOTE: there is no "PAID" delivery_status. Payment state lives in
  // `isPaid`/`paymentStatus` (see utilities/paymentStatus.js); the
  // delivery_status enum in models/Orders.js has no such member.
  const getProgress = () => getDeliveryProgress(order);

  const getBadgeStyle = (status) => {
    switch (status) {
      case "PROCESSING":
        return {
          backgroundColor: "orange",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
          width: "110px",
          height: "20px",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
        };
      case "ASSIGNED":
        return {
          backgroundColor: "blue",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DISPATCHED":
        return {
          backgroundColor: "purple",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DELIVERED":
        return {
          backgroundColor: "green",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "REJECTED":
        return {
          backgroundColor: "red",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      default:
        return {
          backgroundColor: "grey",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
    }
  };

  const getPaidBadgeStyle = (isPaid) => {
    return {
      backgroundColor: isPaid ? "green" : "#2C3A40",
      color: "white",
      borderRadius: "10px",
      padding: "3px",
    };
  };

  // Only an order still in PROCESSING can be cancelled here. This used to also
  // accept a "PAID" delivery_status, which is not a member of the delivery_status
  // enum and could never be stored — dead code that made the rule look broader
  // than it is. Behaviour is unchanged.
  const canCancelOrder = order?.delivery_status === "PROCESSING";

  const getDisabledStatusOptions = () => {
    const disabledOptions = {
      PROCESSING: order?.delivery_status !== "PROCESSING",
      ASSIGNED:
        order?.delivery_status !== "PROCESSING" &&
        !canVendorDispatch(order),
      DISPATCHED:
        !canVendorDispatch(order) &&
        !canVendorMarkDelivered(order),
      DELIVERED:
        !canVendorMarkDelivered(order) &&
        order?.delivery_status !== "DELIVERED",
      REJECTED:
        order?.delivery_status === "DELIVERED" ||
        order?.delivery_status === "REJECTED",
    };
    return disabledOptions;
  };

  const disabledStatusOptions = getDisabledStatusOptions();

  useEffect(() => {
    if (order?.delivery_status === "ASSIGNED") {
      const storedAssignedTime = localStorage.getItem(
        `order_${order?._id}_assignedTime`
      );
      if (storedAssignedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedAssignedTime, 10)) / 1000
        );
        setElapsedAssignedTime(elapsed);
      }
    }
    if (order?.delivery_status === "DISPATCHED") {
      const storedDispatchedTime = localStorage.getItem(
        `order_${order?._id}_dispatchedTime`
      );
      if (storedDispatchedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedDispatchedTime, 10)) / 1000
        );
        setElapsedDispatchedTime(elapsed);
      }
    }

    const interval = setInterval(() => {
      if (order?.delivery_status === "ASSIGNED") {
        setElapsedAssignedTime((prev) => prev + 1);
      }
      if (order?.delivery_status === "DISPATCHED") {
        setElapsedDispatchedTime((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.delivery_status, order?._id]);

  // Get brand name from order items
  const getBrandName = () => {
    if (order?.orderItems && order.orderItems.length > 0) {
      const firstItem = order.orderItems[0];
      if (firstItem.vendorId) {
        if (typeof firstItem.vendorId === "object") {
          return firstItem.vendorId.name || firstItem.vendorId.businessName || "Brand";
        }
      }
    }
    // The order's own brand is the truth. Falling back to the LOGGED-IN user's
    // business name only ever worked because the vendor was the sole visitor —
    // platform admins now reach this screen too (brand orders are redirected
    // here from /admin/orderDetails/:id), and for them that fallback labelled
    // the order with the ADMIN's company instead of the brand's.
    if (order?.brandId && typeof order.brandId === "object") {
      return order.brandId.name || order.brandId.businessName || "Brand";
    }
    const viewerRole = String(
      user?.result?.user_role || user?.result?.role || ""
    ).toUpperCase();
    const viewerIsPlatformAdmin =
      user?.result?.isSuperAdmin === true ||
      user?.result?.userType === "LUSUKU STAFF" ||
      ["SUPERUSER", "SUPERADMIN", "ADMIN"].includes(viewerRole);
    if (viewerIsPlatformAdmin) return "Brand";
    return user?.result?.businessName || "Brand";
  };

  if (loading) {
    return (
      <Container>
        <Spinner
          animation="border"
          role="status"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!order || !order.orderItems || order.orderItems.length === 0) {
    return (
      <Container>
        <Alert variant="info">No order details available</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="order-details-container" style={{ fontSize: "0.9rem" }}>
      <Row className="mb-3">
        <Col md="12">
          <Button
            variant="outline-primary"
            onClick={handleBack}
            className="d-flex align-items-center enhanced-button"
            style={{
              background: "linear-gradient(135deg, #013A63 0%, #45a29e 100%)",
              color: "white",
              border: "none",
            }}
          >
            <MdArrowBackIosNew style={{ marginRight: "8px" }} />
            Back to Orders
          </Button>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Card className="shadow enhanced-card">
            <Card.Header
              className="gradient-header rod-card-header-inner"
              style={{
                padding: "20px 25px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "none",
              }}
            >
              <div>
                <Card.Title
                  as="h4"
                  style={{ color: "white", marginBottom: "5px" }}
                >
                  Brand Order Details
                </Card.Title>
                <small style={{ color: "#a8c6df", display: "block" }}>
                  Order #{order?._id}
                </small>
                {order?.createdAt && (
                  <small style={{ color: "#a8c6df", display: "block", marginTop: "2px" }}>
                    {moment(order.createdAt).format("MMM D, YYYY · h:mm a")}
                  </small>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <VendorHandoffActions orderId={order?._id} />
                <Button
                  onClick={handlePrint}
                  className="d-flex align-items-center enhanced-button rod-print-btn"
                  style={{
                    gap: "8px",
                    backgroundColor: "#13678A",
                    color: "white",
                    border: "none",
                  }}
                >
                  <MdLocalPrintshop />
                  Print Receipt
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              {isDataLoaded && order?.orderItems?.length > 0 ? (
                <>
                  <Row>
                    <Col md="6" className="rod-left-col">
                      <h5
                        className="rod-section-title"
                        style={{
                          color: "#023059",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        Order Items
                      </h5>
                      <div className="rod-items-table-wrap">
                      <OrderLinesTable order={order} />
                      </div>
                      <div className="rider-selection-wrapper mt-4">
                        {/* Assignment Mode Toggle */}
                        {!order?.riderId && order?.delivery_status === "PROCESSING" && (
                          <div className="mb-3">
                            <Form.Label style={{ color: "#1e3a8a", fontWeight: "bold" }}>
                              Assignment Type
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Button
                                variant={assignmentMode === "rider" ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => setAssignmentMode("rider")}
                              >
                                Assign to Rider
                              </Button>
                              <Button
                                variant={assignmentMode === "3pl" ? "success" : "outline-success"}
                                size="sm"
                                onClick={() => setAssignmentMode("3pl")}
                              >
                                <MdLocalShipping style={{ marginRight: "5px" }} />
                                Assign to 3PL Partner
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Show 3PL Partner Assignment if in PENDING_3PL status */}
                        {order?.delivery_status === "PENDING_3PL" && (
                          <Alert
                            style={{
                              backgroundColor: "rgba(30, 58, 138, 0.95)",
                              color: "white",
                              borderRadius: "12px",
                              padding: "16px 20px"
                            }}
                            className="mb-3"
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <div className="d-flex align-items-center mb-2">
                                  <MdLocalShipping style={{ marginRight: "8px", fontSize: "20px" }} />
                                  <strong style={{ fontSize: "15px" }}>Pending 3PL Assignment</strong>
                                </div>
                                <div style={{ opacity: 0.9, fontSize: "13px", lineHeight: 1.5 }}>
                                  Order sent to: <strong>{order?.assignmentDetails?.partnerId?.companyName || "Unknown"}</strong>
                                  <br />
                                  Waiting for 3PL to assign a rider.
                                </div>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{
                                  borderRadius: "8px",
                                  padding: "8px 16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontWeight: "500",
                                  borderWidth: "2px",
                                  backgroundColor: "rgba(220, 38, 38, 0.1)",
                                  borderColor: "#dc2626",
                                  color: "#fff"
                                }}
                                onClick={() => setShowRecallModal(true)}
                                disabled={(recalling3PL) || !ordersGate.allowed}
                                title={ordersGate.title}
                              >
                                {recalling3PL ? (
                                  <>
                                    <Spinner animation="border" size="sm" />
                                    Recalling...
                                  </>
                                ) : (
                                  <>
                                    <MdUndo style={{ fontSize: "16px" }} />
                                    Recall Order
                                  </>
                                )}
                              </Button>
                            </div>
                          </Alert>
                        )}

                        {/* Rider Selection (when mode is 'rider') */}
                        {assignmentMode === "rider" && order?.delivery_status !== "PENDING_3PL" && (
                          <RiderSelectionDropdown
                            order={order}
                            riderId={riderId}
                            setRiderId={setRiderId}
                            loadingRiders={loadingRiders}
                            riders={riders}
                            assignRestaurantOrderToRider={assignBrandOrderToRider}
                            elapsedAssignedTime={elapsedAssignedTime}
                            elapsedDispatchedTime={elapsedDispatchedTime}
                            onRiderSelect={(newRiderId, rider) => {
                              setRiderId(newRiderId);
                              setSelectedAssignmentRider(rider || null);
                            }}
                          />
                        )}

                        {/* 3PL Partner Selection */}
                        {assignmentMode === "3pl" && order?.delivery_status === "PROCESSING" && (
                          <>
                            <ThreePLPartnerDropdown
                              dropoff={order?.shippingAddress?.latLng}
                              selectedPartnerId={selectedPartnerId}
                              onSelectPartner={setSelectedPartnerId}
                              disabled={order?.delivery_status !== "PROCESSING"}
                            />
                            {selectedPartnerId && (
                              <Button
                                variant="success"
                                onClick={assignOrderTo3PLPartner}
                                className="mt-2"
                                style={{
                                  width: "100%",
                                  backgroundColor: assigning3PL ? "#4caf50" : "#4caf50",
                                  borderColor: "#4caf50",
                                  color: "white"
                                }}
                                disabled={(!selectedPartnerId || assigning3PL) || !ordersGate.allowed}
                                title={ordersGate.title}
                              >
                                {assigning3PL ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" style={{ color: "white" }} />
                                    <span style={{ color: "white" }}>Assigning...</span>
                                  </>
                                ) : (
                                  <>
                                    <MdLocalShipping style={{ marginRight: "5px" }} />
                                    Send to 3PL Partner
                                  </>
                                )}
                              </Button>
                            )}
                          </>
                        )}

                        {/* Re-assign button (for already assigned orders) */}
                        {order?.delivery_status !== "PROCESSING" &&
                          order?.delivery_status !== "PENDING_3PL" &&
                          !isRiderAssignmentLocked(order) && (
                            <Button
                              disabled={!ordersGate.allowed}
                              title={ordersGate.title}
                              variant="secondary"
                              onClick={handleReassignOrder}
                              className="mt-2"
                              style={{ width: "100%" }}
                            >
                              <MdRefresh style={{ marginRight: "5px" }} />
                              Re-assign Order to different Rider!
                            </Button>
                          )}
                      </div>
                    </Col>

                    <Col md="6" className="rod-right-col">
                      <h5
                        className="rod-section-title"
                        style={{
                          color: "#023059",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        Order Summary
                      </h5>
                      <Card
                        className="mb-3 p-3 shadow"
                        style={{ boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)" }}
                      >
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Brand:
                          </strong>
                          { } {getBrandName()}
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Customer Name:
                          </strong>
                          { } {order?.customerName || "N/A"}
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Payment Method:
                          </strong>
                          { } {order?.paymentMethod || "N/A"}
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Items Price:
                          </strong>
                          { } Ush{" "}
                          {Number(order?.itemsPrice || 0).toLocaleString("en-UG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Delivery Fee:
                          </strong>
                          { } Ush{" "}
                          {Number(order?.deliveryFee ?? order?.shippingCost ?? 0).toLocaleString("en-UG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Total Price:
                          </strong>
                          { } Ush{" "}
                          {Number(
                            (order?.itemsPrice || 0) + (order?.deliveryFee || order?.shippingCost || 0)
                          ).toLocaleString("en-UG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Payment Status:
                          </strong>{" "}
                          <Badge
                            overlap="rectangular"
                            style={getPaidBadgeStyle(order?.isPaid)}
                          >
                            {order?.isPaid ? (
                              <MdOutlineCreditCard
                                style={{
                                  fontSize: "14px",
                                  verticalAlign: "middle",
                                }}
                              />
                            ) : (
                              <MdOutlineCreditCardOff
                                style={{
                                  fontSize: "14px",
                                  verticalAlign: "middle",
                                }}
                              />
                            )}
                          </Badge>
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Order Status:
                          </strong>{" "}
                          <Badge
                            overlap="rectangular"
                            style={getBadgeStyle(order?.delivery_status)}
                          >
                            {order?.delivery_status || "PROCESSING"}
                          </Badge>
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "14px",
                            }}
                          >
                            Order Date:
                          </strong>
                          { }{" "}
                          {order?.createdAt
                            ? moment(order.createdAt).format(
                              "MMMM Do YYYY, h:mm:ss a"
                            )
                            : "N/A"}
                        </p>
                      </Card>

                      {/* ── Quote Order Fulfilment Panel ── */}
                      {order?.isBrandOrder && order?.brandFlowType === "QUOTE" && (() => {
                        const stages = [
                          { key: "AWAITING_VENDOR_FULFILMENT", label: "Awaiting" },
                          { key: "VENDOR_CONFIRMED",           label: "Confirmed" },
                          { key: "DISPATCHED",                 label: "Dispatched" },
                          { key: "DELIVERED",                  label: "Delivered" },
                        ];
                        const curIdx  = stages.findIndex((s) => s.key === order.brandFlowStage);
                        const action  = QUOTE_STAGE_ACTIONS.find((a) => a.from === order.brandFlowStage);
                        const canCancel = ![ "DELIVERED", "CANCELLED" ].includes(order.brandFlowStage);
                        return (
                          <>
                            <h5 style={{ color: "#023059", fontSize: "18px", fontWeight: "bold", marginTop: 16 }}>
                              Fulfilment Progress
                            </h5>
                            <Card className="mb-3 p-3 shadow" style={{ borderLeft: "4px solid #0a578b" }}>
                              {/* Stage stepper */}
                              <div style={{ display: "flex", gap: 0, marginBottom: 12 }}>
                                {stages.map((s, i) => (
                                  <div key={s.key} style={{ flex: 1, textAlign: "center" }}>
                                    <div style={{
                                      height: 6,
                                      background: i <= curIdx ? "#0a578b" : "#e2e8f0",
                                      borderRadius: i === 0 ? "3px 0 0 3px" : i === stages.length - 1 ? "0 3px 3px 0" : 0,
                                      marginBottom: 4,
                                    }} />
                                    <div style={{ fontSize: "0.68rem", color: i === curIdx ? "#0a578b" : i < curIdx ? "#475569" : "#94a3b8", fontWeight: i === curIdx ? 700 : 400 }}>
                                      {s.label}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {/* Action buttons */}
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {action && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={(stageBusy) || !ordersGate.allowed}
                                    title={ordersGate.title}
                                    onClick={() => advanceQuoteOrderStage(action.stage)}
                                    style={{ background: action.color, borderColor: action.color }}
                                  >
                                    {stageBusy ? <Spinner size="sm" animation="border" /> : action.label}
                                  </Button>
                                )}
                                {canCancel && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={(stageBusy) || !ordersGate.allowed}
                                    title={ordersGate.title}
                                    onClick={() => advanceQuoteOrderStage("CANCELLED")}
                                  >
                                    Cancel Order
                                  </Button>
                                )}
                                {order.brandFlowStage === "DELIVERED" && (
                                  <Alert variant="success" className="mb-0 py-1 px-3 small">Delivered ✓</Alert>
                                )}
                                {order.brandFlowStage === "CANCELLED" && (
                                  <Alert variant="danger" className="mb-0 py-1 px-3 small">Cancelled</Alert>
                                )}
                              </div>
                            </Card>
                          </>
                        );
                      })()}

                      {/* ── Vendor Details ── */}
                      {(() => {
                        const fi = order?.orderItems?.[0];
                        const v = typeof fi?.vendorId === "object" ? fi.vendorId : null;
                        const rd = fi?.restaurantData ?? null;
                        const name = v?.name || rd?.name;
                        if (!name) return null;
                        return (
                          <>
                            <h5 style={{ color: "#023059", fontSize: "20px", fontWeight: "bold", marginTop: 16 }}>
                              Vendor Details
                            </h5>
                            <Card
                              className="mb-3 p-3 shadow"
                              style={{ boxShadow: "0px 4px 15px rgba(0,0,0,0.15)", borderLeft: "4px solid #013A63" }}
                            >
                              <p style={{ fontSize: "14px", marginBottom: 6 }}>
                                <strong style={{ color: "#012030" }}>Brand / Vendor:</strong>{" "}{name}
                              </p>
                              {(v?.address || rd?.address) && (
                                <p style={{ fontSize: "14px", marginBottom: 6 }}>
                                  <strong style={{ color: "#012030" }}>Address:</strong>{" "}{v?.address || rd?.address}
                                </p>
                              )}
                              {/* Brand orders routed to a branch carry fulfilmentOutlet
                                  { _id, name, address }; orders fulfilled by the brand
                                  itself have none and the block reads as before. */}
                              {order?.fulfilmentOutlet?.name && (
                                <p style={{ fontSize: "14px", marginBottom: 6 }}>
                                  <strong style={{ color: "#012030" }}>Fulfilled from:</strong>{" "}
                                  {order.fulfilmentOutlet.name}
                                  {order.fulfilmentOutlet.address ? `, ${order.fulfilmentOutlet.address}` : ""}
                                </p>
                              )}
                              {v?.phone && (
                                <p style={{ fontSize: "14px", marginBottom: 6 }}>
                                  <strong style={{ color: "#012030" }}>Phone:</strong>{" "}{v.phone}
                                </p>
                              )}
                              {v?.email && (
                                <p style={{ fontSize: "14px", marginBottom: 6 }}>
                                  <strong style={{ color: "#012030" }}>Email:</strong>{" "}{v.email}
                                </p>
                              )}
                              {order?.brandFlowType && (
                                <p style={{ fontSize: "14px", marginBottom: 6 }}>
                                  <strong style={{ color: "#012030" }}>Order Type:</strong>{" "}
                                  <span style={{ background: "#013A63", color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                                    {order.brandFlowType}
                                  </span>
                                </p>
                              )}
                              {order?.selectedEditionDate && (
                                <p style={{ fontSize: "14px", marginBottom: 0 }}>
                                  <strong style={{ color: "#012030" }}>Edition Date:</strong>{" "}
                                  <span style={{ color: "#017109", fontWeight: 600 }}>
                                    {moment(order.selectedEditionDate).format("MMMM Do YYYY")}
                                  </span>
                                </p>
                              )}
                            </Card>
                          </>
                        );
                      })()}

                      <h5
                        style={{
                          color: "#023059",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        Shipping Address
                      </h5>
                      <Card
                        className="p-3 shadow"
                        style={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)" }}
                      >
                        <p style={{ fontSize: "14px" }}>
                          {getShippingAddressText(order?.shippingAddress, "No address recorded")}
                        </p>
                        <ContactAction
                          label="Customer"
                          name={order?.customerName}
                          phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                          compact
                          style={{ marginBottom: 10 }}
                        />

                        {/* The shared drop-off block (place name, precision,
                            Show map, Send pin, Copy pin); the route map modal
                            below stays behind its own button. */}
                        {Number.isFinite(Number(order?.shippingAddress?.latLng?.lat)) &&
                          Number.isFinite(Number(order?.shippingAddress?.latLng?.lng)) && (
                            <>
                              <DropoffMap
                                lat={order.shippingAddress.latLng.lat}
                                lng={order.shippingAddress.latLng.lng}
                                address={getShippingAddressText(order?.shippingAddress, "")}
                                shippingAddress={order?.shippingAddress}
                                orderRef={order?.order_number || order?._id}
                                rider={assignedRider || order?.riderId}
                                country={order?.country}
                              />
                              <Button
                                onClick={handleShowMap}
                                style={{ marginTop: 8, backgroundColor: "#083860", color: "white", border: "none", fontSize: 13 }}
                              >
                                <MdMap size={18} style={{ marginRight: 6 }} />
                                Delivery route
                              </Button>
                            </>
                          )}
                      </Card>

                      {/* Map Modal */}
                      <Modal show={showMapModal} onHide={handleCloseMap} size="lg">
                        <Modal.Header
                          closeButton
                          style={{ background: "linear-gradient(135deg, #013A63 0%, #45a29e 100%)", color: "#fff", borderBottom: "none" }}
                        >
                          <Modal.Title>
                            Delivery route: {getShippingAddressText(order?.shippingAddress, "Delivery map")}
                          </Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ padding: 0 }}>
                          {(() => {
                            const destLat = order?.shippingAddress?.latLng?.lat;
                            const destLng = order?.shippingAddress?.latLng?.lng;
                            const firstItem = order?.orderItems?.[0];
                            const vendor = typeof firstItem?.vendorId === "object" ? firstItem.vendorId : null;
                            const restaurantData = firstItem?.restaurantData ?? null;
                            const originLat = vendor?.latitude ?? restaurantData?.latitude ?? null;
                            const originLng = vendor?.longitude ?? restaurantData?.longitude ?? null;
                            const vendorName = vendor?.name || restaurantData?.name || vendor?.businessName || "Pickup";
                            const customerArea = order?.shippingAddress?.locationName?.vicinity || order?.shippingAddress?.locationName?.name || "Customer";

                            if (!destLat) {
                              return (
                                <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
                                  No customer coordinates available for this order.
                                </div>
                              );
                            }

                            const hasOrigin = originLat !== null && originLng !== null;
                            const vendorAddress = vendor?.address || restaurantData?.address || "";
                            const vendorPhone = vendor?.phone || "";
                            const customerName = order?.customerName || order?.client_id?.name || "Customer";
                            const customerPhone = order?.shippingAddress?.customerNumber || "";
                            const customerAddress = order?.shippingAddress?.address || customerArea;

                            const leafletHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    html, body, #map { width:100%; height:100%; font-family: 'Segoe UI', sans-serif; }
    /* route info pill */
    .info-pill { position:absolute; top:12px; left:50%; transform:translateX(-50%); z-index:1000;
      background:linear-gradient(135deg,#013A63,#1a6fa8); color:#fff; padding:7px 18px;
      border-radius:24px; font-size:12px; font-weight:700; white-space:nowrap;
      box-shadow:0 3px 12px rgba(1,58,99,0.45); letter-spacing:.3px; }
    /* legend */
    .legend { position:absolute; bottom:28px; left:12px; z-index:1000;
      background:rgba(255,255,255,0.96); border-radius:10px; padding:10px 14px;
      box-shadow:0 2px 10px rgba(0,0,0,0.18); font-size:11px; line-height:1.8; min-width:140px; }
    .legend-dot { display:inline-block; width:11px; height:11px; border-radius:50%; margin-right:6px; vertical-align:middle; border:2px solid #fff; box-shadow:0 1px 3px rgba(0,0,0,0.3); }
    /* popups */
    .pickup-popup .leaflet-popup-content-wrapper { background:#013A63; color:#fff; border-radius:12px; padding:0; box-shadow:0 4px 16px rgba(1,58,99,0.4); }
    .pickup-popup .leaflet-popup-tip { background:#013A63; }
    .pickup-popup .leaflet-popup-content { margin:0; }
    .delivery-popup .leaflet-popup-content-wrapper { background:#16a34a; color:#fff; border-radius:12px; padding:0; box-shadow:0 4px 16px rgba(22,163,74,0.4); }
    .delivery-popup .leaflet-popup-tip { background:#16a34a; }
    .delivery-popup .leaflet-popup-content { margin:0; }
    .popup-inner { padding:10px 14px; min-width:160px; }
    .popup-title { font-size:13px; font-weight:700; margin-bottom:4px; }
    .popup-row { font-size:11px; opacity:.88; margin-bottom:2px; display:flex; align-items:flex-start; gap:5px; }
    .popup-row span { opacity:.7; font-size:10px; min-width:14px; }
  </style>
</head>
<body>
  <div class="info-pill" id="routeInfo">Loading route…</div>
  <div id="map"></div>
  <div class="legend" id="legend" style="display:none">
    <div><span class="legend-dot" style="background:#013A63"></span><b>Pickup</b></div>
    <div><span class="legend-dot" style="background:#16a34a"></span><b>Delivery</b></div>
    <div style="margin-top:6px;border-top:1px solid #e2e8f0;padding-top:6px">
      <svg width="28" height="8" style="vertical-align:middle;margin-right:4px"><line x1="0" y1="4" x2="28" y2="4" stroke="#013A63" stroke-width="3" stroke-linecap="round"/></svg>Route
    </div>
  </div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <script>
    const destLat=${destLat}, destLng=${destLng};
    const hasOrigin=${hasOrigin};
    const originLat=${hasOrigin ? originLat : destLat};
    const originLng=${hasOrigin ? originLng : destLng};
    const vendorName=${JSON.stringify(vendorName)};
    const vendorAddress=${JSON.stringify(vendorAddress)};
    const vendorPhone=${JSON.stringify(vendorPhone)};
    const customerArea=${JSON.stringify(customerArea)};
    const customerName=${JSON.stringify(customerName)};
    const customerPhone=${JSON.stringify(customerPhone)};
    const customerAddress=${JSON.stringify(customerAddress)};

    const map = L.map('map', { zoomControl:true });
    L.tileLayer(${JSON.stringify(BASEMAP_TILE_URL)}, {
      attribution:${JSON.stringify(BASEMAP_ATTRIBUTION)},
      subdomains:${JSON.stringify(BASEMAP_SUBDOMAINS)}, maxZoom:${BASEMAP_MAX_ZOOM}
    }).addTo(map);

    const pickupSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path d="M16 0C7.163 0 0 7.163 0 16c0 10.5 16 26 16 26S32 26.5 32 16C32 7.163 24.837 0 16 0z" fill="#013A63" stroke="#fff" stroke-width="2"/><circle cx="16" cy="16" r="7" fill="#fff" opacity="0.9"/><text x="16" y="20" text-anchor="middle" fill="#013A63" font-size="10" font-weight="900" font-family="sans-serif">🏠</text></svg>';
    const deliverySvg = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path d="M16 0C7.163 0 0 7.163 0 16c0 10.5 16 26 16 26S32 26.5 32 16C32 7.163 24.837 0 16 0z" fill="#16a34a" stroke="#fff" stroke-width="2"/><circle cx="16" cy="16" r="7" fill="#fff" opacity="0.9"/><text x="16" y="20" text-anchor="middle" fill="#16a34a" font-size="10" font-weight="900" font-family="sans-serif">📦</text></svg>';

    const pickupIcon = L.divIcon({ html: pickupSvg, className:'', iconSize:[32,42], iconAnchor:[16,42], popupAnchor:[0,-40] });
    const deliveryIcon = L.divIcon({ html: deliverySvg, className:'', iconSize:[32,42], iconAnchor:[16,42], popupAnchor:[0,-40] });

    const deliveryPopupHtml = '<div class="popup-inner">'
      + '<div class="popup-title">📦 ' + customerName + '</div>'
      + (customerAddress ? '<div class="popup-row"><span>📍</span>' + customerAddress + '</div>' : '')
      + (customerPhone ? '<div class="popup-row"><span>📞</span>' + customerPhone + '</div>' : '')
      + '<div class="popup-row" style="margin-top:4px;opacity:.6;font-size:10px">Delivery Destination</div>'
      + '</div>';

    const destMarker = L.marker([destLat, destLng], { icon: deliveryIcon })
      .addTo(map).bindPopup(deliveryPopupHtml, { className:'delivery-popup', maxWidth:220 });

    (function() {
      if (!hasOrigin) {
        map.setView([destLat, destLng], 15);
        document.getElementById('routeInfo').textContent = '📍 Delivery only (no vendor coords saved)';
        destMarker.openPopup();
        document.getElementById('legend').style.display = 'block';
        return;
      }

      const pickupPopupHtml = '<div class="popup-inner">'
        + '<div class="popup-title">🏥 ' + vendorName + '</div>'
        + (vendorAddress ? '<div class="popup-row"><span>📍</span>' + vendorAddress + '</div>' : '')
        + (vendorPhone ? '<div class="popup-row"><span>📞</span>' + vendorPhone + '</div>' : '')
        + '<div class="popup-row" style="margin-top:4px;opacity:.6;font-size:10px">Pickup / Dispatch Point</div>'
        + '</div>';

      const originMarker = L.marker([originLat, originLng], { icon: pickupIcon })
        .addTo(map).bindPopup(pickupPopupHtml, { className:'pickup-popup', maxWidth:220 });

      map.fitBounds([[originLat, originLng],[destLat, destLng]], { padding:[50,50] });
      originMarker.openPopup();

      fetch('https://router.project-osrm.org/route/v1/driving/' + originLng + ',' + originLat + ';' + destLng + ',' + destLat + '?overview=full&geometries=geojson&steps=false')
        .then(r => r.json())
        .then(data => {
          const route = data.routes[0];
          const coords = route.geometry.coordinates.map(c => [c[1], c[0]]);
          /* shadow line */
          L.polyline(coords, { color:'rgba(0,0,0,0.15)', weight:8, opacity:1, lineJoin:'round', lineCap:'round' }).addTo(map);
          /* main route */
          L.polyline(coords, { color:'#013A63', weight:5, opacity:0.9, lineJoin:'round', lineCap:'round' }).addTo(map);
          /* highlight overlay */
          L.polyline(coords, { color:'#4fa3e0', weight:2, opacity:0.6, lineJoin:'round', lineCap:'round', dashArray:'1,8' }).addTo(map);
          const dist = (route.distance / 1000).toFixed(1);
          const mins = Math.round(route.duration / 60);
          document.getElementById('routeInfo').textContent = '� ' + dist + ' km  ·  ⏱ ~' + mins + ' min drive';
          map.fitBounds(L.polyline(coords).getBounds(), { padding:[48,48] });
          document.getElementById('legend').style.display = 'block';
        })
        .catch(() => {
          L.polyline([[originLat, originLng],[destLat, destLng]], { color:'#013A63', weight:4, dashArray:'10,7', opacity:0.7, lineJoin:'round' }).addTo(map);
          document.getElementById('routeInfo').textContent = '📍 Straight-line estimate (router unavailable)';
          document.getElementById('legend').style.display = 'block';
        });
    })();
  </script>
</body>
</html>`;

                            const blob = new Blob([leafletHtml], { type: "text/html" });
                            const blobUrl = URL.createObjectURL(blob);

                            return (
                              <iframe
                                key={order?._id}
                                src={blobUrl}
                                width="100%"
                                height="480"
                                frameBorder="0"
                                style={{ display: "block", border: "none" }}
                                title="Delivery Route"
                                onLoad={() => URL.revokeObjectURL(blobUrl)}
                              />
                            );
                          })()}
                        </Modal.Body>
                        <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", flexDirection: "column", alignItems: "stretch", gap: 10, padding: "12px 16px" }}>
                          {/* Vendor + Customer detail strip */}
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            {/* Vendor card */}
                            {(() => {
                              const fi = order?.orderItems?.[0];
                              const v = typeof fi?.vendorId === "object" ? fi.vendorId : null;
                              const rd = fi?.restaurantData ?? null;
                              const name = v?.name || rd?.name || null;
                              if (!name) return null;
                              return (
                                <div style={{ flex: 1, minWidth: 180, background: "#e8f0fe", borderRadius: 8, padding: "8px 12px", borderLeft: "3px solid #013A63" }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "#013A63", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 3 }}>🏭 Vendor / Pickup</div>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{name}</div>
                                  {(v?.address || rd?.address) && <div style={{ fontSize: 11, color: "#475569", marginTop: 1 }}>📍 {v?.address || rd?.address}</div>}
                                  {v?.phone && <div style={{ fontSize: 11, color: "#475569" }}>📞 {v.phone}</div>}
                                </div>
                              );
                            })()}
                            {/* Customer card */}
                            <div style={{ flex: 1, minWidth: 180, background: "#f0fdf4", borderRadius: 8, padding: "8px 12px", borderLeft: "3px solid #16a34a" }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 3 }}>📦 Customer / Delivery</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{order?.customerName || order?.client_id?.name || "Customer"}</div>
                              {(order?.shippingAddress?.address || order?.shippingAddress?.locationName?.name) &&
                                <div style={{ fontSize: 11, color: "#475569", marginTop: 1 }}>📍 {order.shippingAddress.address || order.shippingAddress.locationName?.name}</div>}
                              {order?.shippingAddress?.customerNumber && <div style={{ fontSize: 11, color: "#475569" }}>� {order.shippingAddress.customerNumber}</div>}
                              {order?.shippingAddress?.latLng?.lat && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{order.shippingAddress.latLng.lat.toFixed(4)}, {order.shippingAddress.latLng.lng.toFixed(4)}</div>}
                            </div>
                          </div>
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="primary" onClick={handleCloseMap} style={{ background: "#013A63", border: "none", borderRadius: 8, padding: "6px 20px", fontSize: 13 }}>
                              Close
                            </Button>
                          </div>
                        </Modal.Footer>
                      </Modal>
                      {/* What to do next, and who the order is waiting on.
                          This page has no progress bar, so the banner sits
                          directly above the control it is talking about. */}
                      <OrderNextStep order={order} style={{ marginTop: "16px" }} />
                      <div className="mt-4">
                        <Form.Group
                          controlId="formOrderStatus"
                          className="shadow p-3"
                          style={{ boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)" }}
                        >
                          <Form.Label
                            style={{ color: "#012030", fontWeight: "bold" }}
                          >
                            Update Order Status
                          </Form.Label>
                          <AppSelect block
                            disabled={!ordersGate.allowed}
                            title={ordersGate.title}
                            value={order?.delivery_status || "PROCESSING"}
                            onChange={(e) => handleUpdateStatus(e.target.value)}
                          >
                            <option
                              value="PROCESSING"
                              disabled={disabledStatusOptions.PROCESSING}
                            >
                              1. Processing
                            </option>
                            <option
                              value="ASSIGNED"
                              disabled={disabledStatusOptions.ASSIGNED}
                            >
                              2. Assigned
                            </option>
                            <option
                              value="DISPATCHED"
                              disabled={disabledStatusOptions.DISPATCHED}
                            >
                              3. Dispatched
                            </option>
                            <option
                              value="DELIVERED"
                              disabled={disabledStatusOptions.DELIVERED}
                            >
                              4. Delivered
                            </option>
                            <option
                              value="REJECTED"
                              disabled={disabledStatusOptions.REJECTED}
                            >
                              5. Cancel Order
                            </option>
                          </AppSelect>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col className="d-flex justify-content-end align-items-center rod-action-row">
                      <div
                        style={{
                          cursor: !canCancelOrder ? "not-allowed" : "pointer",
                          backgroundColor: !canCancelOrder
                            ? "#8C1F28"
                            : "transparent",
                          borderRadius: "5px",
                          padding: "2px",
                          marginRight: "5px",
                          width: "160px",
                          height: "40px",
                        }}
                      >
                        <Button
                          variant="danger"
                          onClick={() => setShowCancelModal(true)}
                          className="mr-2"
                          disabled={(!canCancelOrder) || !ordersGate.allowed}
                          title={ordersGate.title}
                          style={{
                            backgroundColor: !canCancelOrder
                              ? "transparent"
                              : "red",
                            color: !canCancelOrder ? "white" : "white",
                            border: "none",
                            fontSize: "14px",
                            width: "100%",
                            height: "100%",
                            padding: "6px",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.9)",
                          }}
                        >
                          <MdCancel
                            style={{
                              fontSize: "20px",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: "5px",
                            }}
                          />
                          Cancel Order
                        </Button>
                      </div>
                      {/* components/Orders/ContactAction: a real tel: link on
                          a phone, copy on a desktop. */}
                      <ContactAction
                        label="Customer"
                        name={order?.customerName}
                        phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                        style={{ marginRight: 5 }}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col md="12">
                      {(() => {
                        const stages = ["PROCESSING", "ASSIGNED", "DISPATCHED", "DELIVERED"];
                        const labels = ["Processing", "Assigned", "Dispatched", "Delivered"];
                        const currentIdx = stages.indexOf(order?.delivery_status ?? "PROCESSING");
                        const isRejected = order?.delivery_status === "REJECTED";
                        return (
                          <div style={{ display: "flex", alignItems: "center", padding: "8px 0" }}>
                            {stages.map((stage, i) => {
                              const done = !isRejected && currentIdx >= i;
                              const active = !isRejected && currentIdx === i;
                              return (
                                <React.Fragment key={stage}>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 auto" }}>
                                    <div style={{
                                      width: 32, height: 32, borderRadius: "50%",
                                      background: isRejected ? "#ef4444" : done ? "#22c55e" : "#e2e8f0",
                                      border: active ? "3px solid #16a34a" : "2px solid transparent",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      color: done || isRejected ? "#fff" : "#94a3b8",
                                      fontWeight: 700, fontSize: 13,
                                      boxShadow: active ? "0 0 0 4px rgba(34,197,94,0.2)" : "none",
                                      transition: "all 0.3s",
                                    }}>
                                      {isRejected ? "✕" : done ? "✓" : i + 1}
                                    </div>
                                    <span style={{ fontSize: 11, marginTop: 5, fontWeight: active ? 700 : 500, color: active ? "#16a34a" : done ? "#15803d" : "#94a3b8", whiteSpace: "nowrap" }}>
                                      {labels[i]}
                                    </span>
                                  </div>
                                  {i < stages.length - 1 && (
                                    <div style={{ flex: 1, height: 3, background: !isRejected && currentIdx > i ? "#22c55e" : "#e2e8f0", margin: "0 4px", marginBottom: 20, borderRadius: 2, transition: "background 0.3s" }} />
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col md="12" className="d-flex justify-content-between rod-dispatch-row">
                      <div
                        style={{
                          cursor:
                            !canVendorDispatch(order)
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            !canVendorDispatch(order)
                              ? "#4B4952"
                              : "transparent",
                          borderRadius: "5px",
                          padding: "2px",
                        }}
                      >
                        <Button
                          variant="warning"
                          onClick={handleDispatchBrandOrder}
                          disabled={(!canVendorDispatch(order)) || !ordersGate.allowed}
                          title={ordersGate.title}
                          style={{
                            backgroundColor:
                              !canVendorDispatch(order)
                                ? "transparent"
                                : "orange",
                            color:
                              !canVendorDispatch(order)
                                ? "white"
                                : "black",
                            border: "none",
                            fontSize: "14px",
                            boxShadow: "0 2px 5px rgb(236, 234, 232)",
                          }}
                        >
                          Dispatch Order
                        </Button>
                      </div>
                      <div
                        style={{
                          cursor:
                            !canVendorMarkDelivered(order)
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            !canVendorMarkDelivered(order)
                              ? "green"
                              : "transparent",
                          borderRadius: "5px",
                          padding: "2px",
                        }}
                      >
                        <Button
                          variant="success"
                          onClick={handleDeliverBrandOrder}
                          disabled={(!canVendorMarkDelivered(order)) || !ordersGate.allowed}
                          title={ordersGate.title}
                          style={{
                            backgroundColor:
                              !canVendorMarkDelivered(order)
                                ? "transparent"
                                : "green",
                            color:
                              !canVendorMarkDelivered(order)
                                ? "white"
                                : "black",
                            border: "none",
                            fontSize: "14px",
                            boxShadow: "0 2px 5px rgb(236, 234, 232)",
                          }}
                        >
                          Mark as Delivered
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showCancelModal} onHide={() => { setShowCancelModal(false); cancelReason.reset(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel this order?
          <CancelOrderFields state={cancelReason} isPaid={!!order?.isPaid} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Close
          </Button>
          <Button variant="danger" onClick={handleCancelOrder}>
            Cancel Order
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recall from 3PL Confirmation Modal */}
      <Modal show={showRecallModal} onHide={() => setShowRecallModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Recall Order from 3PL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to recall this order from{" "}
          <strong>{order?.assignmentDetails?.partnerId?.companyName || "the 3PL partner"}</strong>?
          <br />
          <small className="text-muted">
            The order will be removed from the 3PL partner's queue and returned to PROCESSING status.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecallModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={recallOrderFrom3PL}
            disabled={recalling3PL}
          >
            {recalling3PL ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Recalling...
              </>
            ) : (
              "Recall Order"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BrandVendorOrderDetails;
